import {
  setPwaServiceWorkerRegistration,
  setPwaRegistrationError,
} from "src/config/pwaRegistration";
import { APP_UPDATE_AVAILABLE_EVENT } from "src/utils/appUpdateEvents";

/** Stored `updateSW` so a user gesture (update banner) can apply the update. */
let updateSwRef = null;
let updateRequested = false;
let registrationPromise = null;

/**
 * Apply the pending Service Worker update (skipWaiting + reload). Called from
 * WebUpdateBanner when the user explicitly accepts the new version.
 */
export function applyPwaUpdate() {
  updateRequested = true;
  if (updateSwRef) {
    updateSwRef(true);
  } else {
    window.location.reload();
  }
}

export async function registerPwaClient() {
  if (typeof window === "undefined") return;
  if (!registrationPromise) registrationPromise = registerPwaClientOnce();
  return registrationPromise;
}

async function registerPwaClientOnce() {
  // Dynamic import keeps the PWA code out of the main bundle; "virtual:" is
  // whitelisted in eslint.config.js (import/no-unresolved ignore).
  const { registerSW } = await import("virtual:pwa-register");

  // `registerSW` returns a function that can trigger `skipWaiting` + reload.
  const updateSW = registerSW({
    immediate: true,
    onNeedReload() {
      // The worker may already have been activated by another tab. In that
      // case a later click should reload, not message a nonexistent waiter.
      updateSwRef = null;
      // Accepting an update in one tab must not reload every other open form.
      if (updateRequested) window.location.reload();
      else window.dispatchEvent(new CustomEvent(APP_UPDATE_AVAILABLE_EVENT));
    },
    onNeedRefresh() {
      // Don't reload silently — the user may have unsaved work in open forms.
      // Surface a controlled "new version" banner instead (WebUpdateBanner).
      updateSwRef = updateSW;
      window.dispatchEvent(new CustomEvent(APP_UPDATE_AVAILABLE_EVENT));
    },
    onOfflineReady() {
      console.warn("[Doitify] App ready to work offline");
    },
    onRegistered(registration) {
      console.warn("[Doitify] Service Worker registered");
      setPwaServiceWorkerRegistration(registration);
      setInterval(() => {
        // `update()` rejects with InvalidStateError when the registration has
        // no active worker (e.g. install failed or it was unregistered).
        // Only poll while the registration is actually valid, and never let a
        // rejection surface as an uncaught error.
        if (registration?.active) {
          registration.update().catch(() => {});
        }
      }, 60000);
    },
    onRegisterError(error) {
      setPwaRegistrationError(error);
      console.warn("[Doitify] Service Worker registration error:", error);
    },
  });

  if (import.meta.env.VITE_NODE_ENV === "production") {
    console.log = () => {};
    console.debug = () => {};
  }
}
